import angular from 'angular';
import PageResponse from './PageResponse';
import { getProvisionalIdentifierForType, indexOfByProp, castToBoolean, normalizeIdentifier } from '../util';
import { createError } from '../error';

export const RESPONSE_STATUS_SUBMITTED = 1;
export const RESPONSE_STATUS_APPROVED = 2;
export const RESPONSE_STATUS_REJECTED = 3;

class RecapResponse {
  constructor() {
    const data = arguments[0] || {};
    this.id = data.id || getProvisionalIdentifierForType('recapresponse');
    this.root_id = data.root_id || null;
    this.allowSave = castToBoolean(data.allowSave, true);
    this.staffing_users_id = data.staffing_users_id || null;
    this.users_id = data.users_id || null;
    this.user_role_id = data.role_variable_id || (data.recap_definition && data.recap_definition.user_role_id) || null;
    this.current_location = data.recap_definition && data.recap_definition.current_location || null;
    this.events_id = data.events_id || null;
    this.event = data.event || null;
    this.position = data.position || null;
    this.recap_definition = data.recap_definition || null;
    this.events_recaps_id = data.recap_definition && data.recap_definition.id || data.events_recaps_id || null;
    this.field_responses = data.field_responses || [];
    this.form_response_sets = data.form_response_sets || [];
    // Saved Additional Comments, for forms whose response set is created later with no saved responses.
    this.form_comments = data.form_comments || [];
    this.status_id = data.status_id || null;
    this.pageResponseMap = {};
    this.enqueued = data.enqueued || false;
    this.preview_location_variables = data.preview_location_variables || null;
    this.events_recaps_external_links_views_id = data.events_recaps_external_links_views_id || null;
    this.events_recaps_version = data.events_recaps_version || null;
    this.events_recaps_updated_at = data.events_recaps_updated_at || null;
    this.declined_by_name = data.declined_by_name || null;
    this.declined_at = data.declined_at || null;
    this.declined_reason = data.declined_reason || null;
    this.eventTimezoneName = data.eventTimezoneName || null;
    this.started_at = data.started_at || null
    this.ended_at = data.ended_at || null
    this.coords = data.coords || {
      startCoordinates: {
        lat: null,
        lon: null,
      },
      endCoordinates: {
        lat: null,
        lon: null,
      }
    }

    this._logicFiltersData = data.logic_filters_data || {};
  }

  get logicFiltersData() {
    return this._logicFiltersData;
  }

  set logicFiltersData(logicFiltersData) {
    this._logicFiltersData = logicFiltersData;
  }

  get allPages() {
    return this.recap_definition && this.recap_definition.pages || [];
  }

  get allPageResponses() {
    return this.allPages.map(page => this.getPageResponse(page));
  }

  get visiblePages() {
    if (this._visiblePages === undefined) {
      console.warn("Accessing RecapResponse.visiblePages before visible pages have been initialized");
      return this.allPages;
    }
    return this._visiblePages;
  }

  get visiblePageResponses() {
    return this.visiblePages.map(page => this.getPageResponse(page));
  }

  get isSubmitted() {
    return this.status_id == RESPONSE_STATUS_SUBMITTED;
  }

  get isApproved() {
    return this.status_id == RESPONSE_STATUS_APPROVED;
  }

  get isRejected() {
    return this.status_id == RESPONSE_STATUS_REJECTED;
  }

  get hasResponses() {
    const allFieldResponses = this.collectAllVisibleFieldResponses();
    const hasResponses = allFieldResponses.some(fieldResponse => !!fieldResponse.value);
    console.debug(`RecapResponse.hasResponses returning ${hasResponses} for recap response`, this);
    return hasResponses;
  }

  /**
   * Get the location variables attached to the EVENT - which are derived from the venue.
   *
   * This is separate from the location variables attached to the RECAP, since the recap lists all the LVs that
   * CAN apply, while the event determines which ones actually DO apply.
   *
   * @returns Array of location variables
   */
  get eventLocationVariables() {
    if (this.event) {
      return this.event.location_variables;
    } else if (this.preview_location_variables) {
      return this.preview_location_variables;
    } else {
      return [];
    }
  }

  get eventId() {
    if (this.event) {
      return normalizeIdentifier(this.event.id);
    } else {
      return normalizeIdentifier(this.events_id);
    }
  }

  indexOfVisiblePage(page) {
    let idx = this.visiblePages.indexOf(page);
    if (idx === -1) {
      throw createError({
        name: "PageNotFound",
        message: "Page not found",
        page,
        visiblePages: this.visiblePages,
        recap_definition: this.recap_definition,
      });
    }
    return idx;
  }

  isLastPage(page) {
    let idx = this.indexOfVisiblePage(page);
    return (idx >= this.visiblePages.length - 1);
  }

  isFirstPage(page) {
    let idx = this.indexOfVisiblePage(page);
    return idx === 0;
  }

  getPageResponse(page) {
    if (!(page.id in this.pageResponseMap)) {
      this.pageResponseMap[page.id] = this._createPageResponse(page);
    }
    return this.pageResponseMap[page.id];
  }

  validateByPage() {
    return this.visiblePageResponses.map(pageResponse => pageResponse.validate());
  }

  validate() {
    return this.validateByPage().reduce((pre, cur) => pre.concat(cur), []);
  }

  updateDisplayLogic(changedFieldResponses = null) {
    this.allPageResponses.forEach(pageResponse => pageResponse.updateDisplayLogic(changedFieldResponses));
    this._updateVisiblePages();
  }

  updateLogicModifiers() {
    this.allPageResponses.forEach(pageResponse => pageResponse.updateLogicModifiers());
  }

  /**
   * Retrieve a field response for the specified field instance ID. When availableToLogicConditionOnFormResponseId is
   * specified, the search will be limited to field responses that are "available" to the specified form response ID;
   * meaning that if the specified field instance is on a multi-response form, the search will be limited to field
   * responses on the same form response.
   *
   * This allows for field logic within a multi-response form to reference other fields on the same form, as well as
   * referencing fields on other forms that are not multi-response forms.
   *
   * @param fieldInstanceId Field instance ID to search for
   * @param availableToLogicConditionOnFormResponseId Limit search to field responses that are available to the specified form response ID
   * @returns {*}
   */
  getFieldResponseForFieldInstanceIdAndFormResponseId(fieldInstanceId, availableToLogicConditionOnFormResponseId = null) {
    // Loop over all page responses looking for the specified field instance
    for (const pageResponse of this.allPageResponses) {
      // Loop over all form response sets looking for the specified field instance
      for (const formResponseSet of pageResponse.form_response_sets) {
        // Loop over all form responses in the set, looking for specified field instance
        for (const formResponse of formResponseSet.getOrCreateFormResponses()) {
          // Check if this form response is "available"
          if (!formResponse.form.isSimpleForm) {
            /**
             * In the case of referenced fields within multiple-response forms (any non-simple form is a multiple
             * response form), these are only "available" to other fields within the same form response.
             */
            if (!availableToLogicConditionOnFormResponseId) {
              // No form response ID was specified; fields within this form are not "available" to the caller
              continue;
            } else if (formResponse.id !== availableToLogicConditionOnFormResponseId) {
              // Form response ID does not match; fields within this form response are not "available" to the caller
              continue;
            }
          }

          // Loop over all field responses on this form, looking for the specified field instance
          for (const fieldResponse of formResponse.getOrCreateFieldResponses()) {
            if (fieldResponse.fieldInstance.id == fieldInstanceId) {
              return fieldResponse;
            }
          }
        }
      }

      // Loop over all field responses directly on this page, looking for the specified field instance
      for (const fieldResponse of pageResponse.field_responses) {
        if (fieldResponse.fieldInstance.id == fieldInstanceId) {
          return fieldResponse;
        }
      }
    }

    console.warn("Unable to find field response for field instance id", fieldInstanceId, this);
    return null;
  }

  getFieldResponseForFieldInstanceId(fieldInstanceId) {
    return this.getFieldResponseForFieldInstanceIdAndFormResponseId(fieldInstanceId);
  }

  collectVisibleFormResponses() {
    return this.visiblePageResponses
      .map(pageResponse => pageResponse.collectVisibleFormResponses())
      .reduce((pre, cur) => pre.concat(cur), []);
  }

  collectAllVisibleFieldResponses() {
    // Return all visible field responses - both those directly on a page, and those on a visible form
    const formResponses = this.collectVisibleFormResponses();
    const flatten = list => list.reduce((pre, cur) => pre.concat(cur), []);
    return flatten(formResponses.map(formResponse => formResponse.collectVisibleFieldResponses()));
  }

  collectAllVisibleFieldResponsesMatchingFieldId(fieldId) {
    const allVisibleFieldResponses = this.collectAllVisibleFieldResponses();
    return allVisibleFieldResponses.filter(fieldResponse => {
      const field = fieldResponse && fieldResponse.fieldInstance && fieldResponse.fieldInstance.field;
      return parseInt(field.id) === parseInt(fieldId);
    });
  }

  collectAllVisibleFieldResponseCurrentNumericValuesMatchingFieldId(fieldId) {
    return this.collectAllVisibleFieldResponsesMatchingFieldId(fieldId)
      .map(fieldResponse => fieldResponse.currentNumericValue)
      .filter(value => value !== null && value !== undefined && !Number.isNaN(parseFloat(value)));
  }

  _createPageResponse(page) {
    const data = {
      page: page,
      user_role_id: this.user_role_id,
      current_location: this.current_location,
      recapResponse: this,
      field_responses: [],
      form_response_sets: [],
    };

    // TODO: Separate forms from fields for later ID checks
    const pageFieldInstances = page.items;
    const pageForms = page.items;

    for (const fieldResponse of this.field_responses) {
      const idx = indexOfByProp(pageFieldInstances, fieldResponse.fieldInstance, 'id');
      if (idx !== -1) {
        data.field_responses.push(fieldResponse);
      }
    }

    for (const formResponseSet of this.form_response_sets) {
      const idx = indexOfByProp(pageForms, formResponseSet.form, 'id');
      if (idx !== -1) {
        data.form_response_sets.push(formResponseSet);
      }
    }

    return new PageResponse(data);
  }

  _updateVisiblePages() {
    const visiblePages = this.allPageResponses
      .filter(pageResponse => pageResponse.visible)
      .map(pageResponse => pageResponse.page);
    if (!angular.equals(visiblePages, this._visiblePages)) {
      this._visiblePages = visiblePages;
    }
  }

  touchAllPages() {
    return this.visiblePageResponses.map(pageResponse => pageResponse.touch());
  }

  viewAllPages() {
    return this.visiblePageResponses.map(pageResponse => pageResponse.view());
  }
}

export default RecapResponse;
